import type { AppRouter } from '@ts-rest/core'
import { initClient } from '@ts-rest/core'
import type { ApiEndpointKey } from './endpoints'
import { getApiBaseUrl } from './endpoints'

/**
 * Фабрика типизованого ts-rest клієнта для landing2-ui.
 * Патерн ідентичний teacher-ui `createTsRestClient` — plain fetch, lazy base URL.
 *
 * @param contract — контракт з `@bibleawr/landing2-contract`
 * @param endpoint — ключ ендпоінту (default: 'LANDING')
 *
 * @example
 * ```ts
 * private readonly client = createLandingTsRestClient(coursesContract)
 *
 * async findMany(params) {
 *   const res = await this.client.getCourses({ query: params })
 *   if (res.status !== 200) throw new Error(`Failed: ${res.status}`)
 *   return res.body
 * }
 * ```
 */
export function createLandingTsRestClient<T extends AppRouter> (
  contract: T,
  endpoint: ApiEndpointKey = 'LANDING',
) {
  return initClient(contract, {
    baseUrl: getApiBaseUrl(endpoint),
    baseHeaders: {},
    api: async args => {
      const response = await fetch(args.path, {
        method: args.method,
        headers: args.headers as HeadersInit,
        body: args.body as string | undefined,
        credentials: 'include', // forward httpOnly cookie for authenticated endpoints
      })
      return {
        status: response.status,
        body: await response.json().catch(() => null),
        headers: response.headers,
      }
    },
  })
}
